import {AnimatePresence, m as motion} from 'motion/react'
import React from 'react'
import styled from 'styled-components'

import {
    VatCalculatorActionedAction,
    VatCalculatorActionedField,
    vatCalculatorActioned,
} from '@/analytics/segment'
import Input from '@/components/input'
import {Text} from '@/components/text'
import {ArrowRight} from '@/images/icons/arrow-right'
import deleteIcon from '@/images/icons/delete.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {focusStyles} from '@/styles/utils'
import mq from '@/utils/media-query'

import type {Dispatch, State} from './vat-calculator'

import {Actions} from './vat-calculator'

interface Props {
    results: State['results']
    dispatch: Dispatch
    vatAddedLabel: string
    vatRemovedLabel: string
    amountLabel: string
    totalNetLabel: string
    totalGrossLabel: string
    title: string
}

const presenceAnimation = {
    initial: {opacity: 0},
    animate: {opacity: 1},
    exit: {
        opacity: 0,
        transition: {delay: 0, duration: 0.3, type: 'spring'},
    },
    transition: {
        type: 'spring',
        duration: 0.6,
    },
}

export const ResultsList = ({
    results,
    dispatch,
    amountLabel,
    vatAddedLabel,
    vatRemovedLabel,
    totalNetLabel,
    totalGrossLabel,
    title,
}: Props) => {
    const {copied_to_clipboard} = useGlobalStrings()

    const onRemove = (index: number) => {
        dispatch({
            type: Actions.REMOVE_RESULT,
            value: index,
        })

        vatCalculatorActioned({
            action: VatCalculatorActionedAction.Interacted,
            field: VatCalculatorActionedField.Delete,
        })
    }

    return (
        <AnimatePresence>
            {results.length > 0 && (
                <HeadingWrapper {...presenceAnimation}>
                    <Text $variant="title-base">{title}</Text>
                    <ArrowRight width={20} style={{transform: 'rotate(90deg)'}} />
                </HeadingWrapper>
            )}

            {results.map(({amount, vatRate, total, isVatIncluded, id}) => (
                <Result
                    key={id}
                    {...presenceAnimation}
                    transition={{
                        ...presenceAnimation.transition,
                        delay: results.length > 1 ? 0.1 : 0.3,
                    }}
                >
                    <InputsWrapper>
                        <StyledInput label={amountLabel} type="number" value={amount} readOnly />
                        <StyledInput
                            copyable
                            copiedText={copied_to_clipboard}
                            value={vatRate}
                            label={isVatIncluded ? vatRemovedLabel : vatAddedLabel}
                        />
                        <StyledInput
                            value={total}
                            copyable
                            copiedText={copied_to_clipboard}
                            label={isVatIncluded ? totalNetLabel : totalGrossLabel}
                        />
                    </InputsWrapper>
                    <RemoveButton onClick={() => onRemove(id)} />
                </Result>
            ))}
        </AnimatePresence>
    )
}

const InputsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacings.s20};

    ${mq.min('sm')} {
        flex-direction: row;
        background-color: ${({theme}) => theme.colors.background.grey};
        border-radius: ${({theme}) => theme.radii.arc20};
        padding: ${({theme}) => theme.spacings.s40};
        width: 100%;
    }
`

const Result = styled(motion.create('div'))`
    padding: ${({theme}) => theme.spacings.s20};
    background-color: ${({theme}) => theme.colors.background.grey};
    border-radius: ${({theme}) => theme.radii.arc20};
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacings.s20};

    &:not(:last-child) {
        margin-bottom: ${({theme}) => theme.spacings.s20};
    }

    ${mq.min('sm')} {
        flex-direction: row;
        background-color: transparent;
        padding: 0;

        &:not(:last-child) {
            margin-bottom: ${({theme}) => theme.spacings.s40};
        }
    }
`
const StyledInput = styled(Input)`
    border-color: ${({theme}) => theme.colors.border.secondary};
`

const RemoveButton = styled.button`
    all: unset;
    width: ${({theme}) => theme.spacings.s24};
    height: ${({theme}) => theme.spacings.s24};
    background-image: url(${deleteIcon});
    background-repeat: no-repeat;
    background-position: center;
    align-self: flex-end;

    &:focus {
        ${focusStyles()}
        border-radius: ${({theme}) => theme.radii.arc4};
    }

    ${mq.min('sm')} {
        margin-bottom: ${({theme}) => theme.spacings.s52};
    }
`

const HeadingWrapper = styled(motion.create('div'))`
    display: flex;
    gap: ${({theme}) => theme.spacings.s20};
    margin-bottom: ${({theme}) => theme.spacings.s20};
    margin-top: ${({theme}) => theme.spacings.s40};

    ${mq.min('sm')} {
        margin-bottom: ${({theme}) => theme.spacings.s40};
    }
`
